import { Vector3 } from 'three/webgpu';

/**
 * Cube-sphere parameterisation of a hollow's wall. Each of the six faces has
 * an outward normal N and tangent axes U, V with U × V = N. A face coordinate
 * (u, v) ∈ [-1, 1]² maps to the cube point N + tan(uπ/4)·U + tan(vπ/4)·V; the
 * tangent warp makes cells nearly equal-area. Directions are normalised cube
 * points.
 */
export const FACE_N: readonly Vector3[] = [
  new Vector3(1, 0, 0),
  new Vector3(-1, 0, 0),
  new Vector3(0, 1, 0),
  new Vector3(0, -1, 0),
  new Vector3(0, 0, 1),
  new Vector3(0, 0, -1),
];
export const FACE_U: readonly Vector3[] = [
  new Vector3(0, 0, -1),
  new Vector3(0, 0, 1),
  new Vector3(1, 0, 0),
  new Vector3(1, 0, 0),
  new Vector3(1, 0, 0),
  new Vector3(-1, 0, 0),
];
export const FACE_V: readonly Vector3[] = [
  new Vector3(0, 1, 0),
  new Vector3(0, 1, 0),
  new Vector3(0, 0, -1),
  new Vector3(0, 0, 1),
  new Vector3(0, 1, 0),
  new Vector3(0, 1, 0),
];

const QUARTER_PI = Math.PI / 4;

/** Cube point (not normalised) for face coordinates. */
export function faceToCube(face: number, u: number, v: number, out: Vector3): Vector3 {
  const a = Math.tan(u * QUARTER_PI);
  const b = Math.tan(v * QUARTER_PI);
  return out.copy(FACE_N[face]).addScaledVector(FACE_U[face], a).addScaledVector(FACE_V[face], b);
}

/** Unit direction for face coordinates. */
export function faceToDir(face: number, u: number, v: number, out: Vector3): Vector3 {
  return faceToCube(face, u, v, out).normalize();
}

export interface FaceCoord {
  face: number;
  u: number;
  v: number;
}

/** Inverse mapping: which face a direction falls on, and its (u, v). */
export function dirToFace(d: Vector3, out: FaceCoord): FaceCoord {
  const ax = Math.abs(d.x);
  const ay = Math.abs(d.y);
  const az = Math.abs(d.z);
  let face: number;
  if (ax >= ay && ax >= az) face = d.x > 0 ? 0 : 1;
  else if (ay >= az) face = d.y > 0 ? 2 : 3;
  else face = d.z > 0 ? 4 : 5;
  const n = FACE_N[face].dot(d);
  const a = FACE_U[face].dot(d) / n;
  const b = FACE_V[face].dot(d) / n;
  out.face = face;
  out.u = Math.max(-1, Math.min(1, Math.atan(a) / QUARTER_PI));
  out.v = Math.max(-1, Math.min(1, Math.atan(b) / QUARTER_PI));
  return out;
}

/** Nodes are addressed by (face, level, x, y); x, y ∈ [0, 2^level). */
export function nodeKey(face: number, level: number, x: number, y: number): number {
  // level < 32, x/y < 2^21 → fits in 53 bits.
  return ((face * 32 + level) * 2 ** 21 + x) * 2 ** 21 + y;
}

/** Face-coordinate bounds of a node. */
export function nodeUV(level: number, x: number, y: number): { u0: number; v0: number; size: number } {
  const size = 2 / 2 ** level;
  return { u0: -1 + x * size, v0: -1 + y * size, size };
}

/** Node containing face coordinate (u, v) at a level. */
export function nodeAt(level: number, u: number, v: number): { x: number; y: number } {
  const n = 2 ** level;
  const x = Math.min(n - 1, Math.max(0, Math.floor(((u + 1) / 2) * n)));
  const y = Math.min(n - 1, Math.max(0, Math.floor(((v + 1) / 2) * n)));
  return { x, y };
}
